import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { MessageCard } from '@womcorp/wom-miwom-ui-kit/dist/components/Cards';
import { navigate } from 'gatsby';
import { PaymentHistoryIcon } from '@womcorp/wom-miwom-ui-kit/dist/@theme/assets/svg/pictures';
import Forbidden from '../components/General/Forbidden';
import InvoicesService from '../services/Invoices';
import CustomerService from '../services/Customer';
import { getInvoicesMessage } from '../services/Contentful';
import { GoogleTagManager } from '../helpers';
import useUser from '../hooks/useUser';
import Auth from '../services/Auth';
import PageTemplate, { NavChevron } from '../components/UI/PageTemplate';
import Status from '../components/StatusBar/Status';
import { MessageContainer } from '../components/Invoices/HeaderInvoices/styles/HeaderInvoicesStyled';
import { redirectInternalOrExternal } from '../helpers/Redirect';
import Container from '../components/General/Container';
import Loading from '../components/General/Loading';
import useIntersect from '../hooks/useIntersect';
import { MonthlyInvoices } from '../components/Invoices';
import NotFoundPanel from '../components/Errors/NotFoundPanel';
import {
  ButtonContainerStyled,
  ChevronStyled,
  InvoiceButtonStyled,
} from '../components/Invoices/MonthlyInvoices/styles/MonthlyInvoicesStyled';

const Page = ({ children }) => (
  <PageTemplate seo="Mis Boletas">
    <NavChevron title="Mis Boletas" level="first" />
    {children}
  </PageTemplate>
);

Page.propTypes = {
  children: PropTypes.node.isRequired,
};

const MisBoletas = () => {
  const user = useUser();
  const { isCorporate, isAdmin } = user.user;
  const myAccount = Auth.getAccountId();

  const [state, setState] = useState({
    invoiceStatus: null,
    loading: {
      invoicesStatus: true,
      accounts: true,
      invoicesMessage: true,
    },
    accountList: [myAccount],
    accountSelected: myAccount,
    flush: false,
    messagesList: [],
    errorInvoices: {},
  });

  const {
    invoiceStatus,
    loading,
    accountList,
    accountSelected,
    flush,
    messagesList,
    errorInvoices,
  } = state;

  const elementRef = useRef(null);
  const [inView] = useIntersect(elementRef, { threshold: 0 });

  const setLoading = (key, value) => {
    setState(prev => ({ ...prev, loading: { ...prev.loading, [key]: value } }));
  };

  const getInvoiceStatus = account => {
    if (errorInvoices[account]) {
      setState(prev => ({
        ...prev,
        errorInvoices: {
          ...prev.errorInvoices,
          [account]: {
            ...prev.errorInvoices[account],
            count: prev.errorInvoices[account].count,
            isError: false,
          },
        },
      }));
    }

    setLoading('invoicesStatus', true);
    setState(prev => ({ ...prev, invoiceStatus: null }));

    InvoicesService.getInvoiceStatusDetail({ flush, account })
      .then(([statusData]) =>
        setState(prev => ({
          ...prev,
          invoiceStatus: statusData,
        }))
      )
      .catch(() => {
        setState(prev => ({
          ...prev,
          errorInvoices: {
            ...prev.errorInvoices,
            [account]: {
              ...prev.errorInvoices[account],
              count: (prev.errorInvoices[account].count || 0) + 1,
              isError: true,
            },
          },
        }));
      })
      .finally(() => setLoading('invoicesStatus', false));
  };

  const handleAccount = async account => {
    const selectedAccount =
      typeof account === 'string' ? account : account.target.value;
    if (selectedAccount !== accountSelected) {
      setState(prev => ({
        ...prev,
        accountSelected: selectedAccount,
        invoiceStatus: null,
      }));
    }

    await getInvoiceStatus(selectedAccount);
    setState(prev => ({ ...prev, flush: false }));
  };

  const getInvoicesMessageData = async () => {
    if (!loading.invoicesMessage) setLoading('invoicesMessage', true);

    getInvoicesMessage()
      .then(([{ messages }]) =>
        setState(prev => ({ ...prev, messagesList: messages }))
      )
      .catch(() => {})
      .finally(() => setLoading('invoicesMessage', false));
  };

  const getAccounts = async () => {
    setLoading('accounts', true);

    CustomerService.getCustomerAccountList()
      .then(([{ accounts }]) => {
        if (accounts?.length > 0) {
          const mapAccounts = accounts.map(({ codigoCuenta }) => ({
            label: codigoCuenta,
            value: codigoCuenta,
          }));
          setState(prev => ({
            ...prev,
            accountList: mapAccounts,
            errorInvoices: mapAccounts.reduce((acc, account) => {
              acc[account.value] = { count: 0, isError: false };
              return acc;
            }, {}),
          }));

          handleAccount(accounts[0].codigoCuenta);
        } else {
          setState(prev => ({
            ...prev,
            errorInvoices: {
              [myAccount]: { count: 0, isError: false },
            },
          }));
          handleAccount(myAccount);
        }
      })
      .catch(() => {
        GoogleTagManager.pushToDataLayer({
          event: 'pagina-virtual',
          page: '/status-bar/mis-boletas/error-selector-cuentas',
          pagename: 'Status bar - Mis Boletas - Error Selector Cuentas',
        });
        setState(prev => ({
          ...prev,
          errorInvoices: {
            [myAccount]: { count: 0, isError: false },
          },
        }));
        handleAccount(myAccount);
      })
      .finally(() => setLoading('accounts', false));
  };

  const getData = () => {
    getAccounts();
    getInvoicesMessageData();
  };

  useEffect(() => {
    getData();

    GoogleTagManager.pushToDataLayer({
      event: 'pagina-virtual',
      page: '/mis-boletas/',
      pagename: 'Mis Boletas',
    });
  }, []);

  const isForbidden = isCorporate && !isAdmin;
  const isLoading =
    loading.accounts ||
    loading.invoicesStatus ||
    loading.invoices ||
    loading.invoicesMessage;
  const showInvoices = invoiceStatus?.lastInvoice && invoiceStatus?.debtStatus;

  if (isForbidden) {
    return (
      <Page>
        <Forbidden
          phoneNumber={user.plan.number}
          tag={{
            event: 'pagina-virtual',
            page: '/mis-boletas/',
            pagename: 'Boletas - No Administrador',
          }}
        />
      </Page>
    );
  }

  return (
    <Page>
      <Status
        invoicesStatus={invoiceStatus}
        handleAccount={handleAccount}
        loadingAccounts={loading.accounts}
        loadingInvoices={isLoading}
        accountList={accountList}
        accountSelected={accountSelected}
        retryFunc={() => handleAccount(accountSelected)}
        isError={errorInvoices[accountSelected]?.isError}
        retryCount={errorInvoices[accountSelected]?.count}
      />
      {isLoading ? (
        <Container>
          <Loading />
        </Container>
      ) : (
        <>
          {!errorInvoices[accountSelected]?.isError && (
            <>
              {messagesList.length > 0 && showInvoices && (
                <MessageContainer>
                  {messagesList.map(message => (
                    <MessageCard
                      key={message?.id}
                      variant={message?.type}
                      detail={message?.body}
                      title={message?.title}
                      linkMessage={message?.linkMessage}
                      onLinkClick={
                        message?.url
                          ? () => redirectInternalOrExternal(message?.url)
                          : undefined
                      }
                    />
                  ))}
                </MessageContainer>
              )}
              {showInvoices ? (
                <MonthlyInvoices
                  data={{
                    lastInvoice: invoiceStatus?.lastInvoice,
                    lastSemester: invoiceStatus?.otherInvoices,
                  }}
                  inView={inView}
                  elementRef={elementRef}
                  loadFunc={() => {
                    setState(prev => ({ ...prev, flush: true }));
                    handleAccount(accountSelected);
                  }}
                  selectedAccount={accountSelected}
                />
              ) : (
                <Container>
                  <NotFoundPanel
                    markOptions={{
                      event: 'pagina-virtual',
                      page: '/boletas/',
                      pagename: 'Boletas - Sin Boletas',
                    }}
                    title="Sin boletas"
                    onClick={() => navigate('/home')}
                    buttonText="Volver al inicio"
                  >
                    <span>
                      Aún no se genera el historial de tus boletas. Vuelve aquí
                      cuando recibas tu primera boleta.
                    </span>
                  </NotFoundPanel>
                </Container>
              )}
            </>
          )}
          <ButtonContainerStyled>
            <InvoiceButtonStyled
              onClick={() => {
                navigate('/historiales/historial-de-pago');
              }}
            >
              <PaymentHistoryIcon />
              <div>Historial de pagos</div>
              <ChevronStyled />
            </InvoiceButtonStyled>
          </ButtonContainerStyled>
        </>
      )}
    </Page>
  );
};

export default MisBoletas;
